import type { AxiosInstance } from 'axios';
import { WorkspacesAPI } from '../types/workspaces.api.js';
import type * as Workspaces from './interfaces/workspaces.js';
import { safeIsoDate, safeJsonDate, transformDates } from './transformers/date.js';
import { download } from './utils.js';

export * as brand from './workspaces.brand.js';
export * as fileUpload from './workspaces.file-upload.js';
export * as globalSearch from './workspaces.global-search.js';
export * as folder from './workspaces.folder.js';
export * as object from './workspaces.object.js';

export class WorkspacesClient {
  constructor(private authenticatedClient: AxiosInstance) {}

  /** The workspace of the current account and application (from the request's scope headers), created on first use. */
  fetchApplicationWorkspace = () => {
    return this.authenticatedClient
      .get<Workspaces.FetchApplicationWorkspace.Response>('/workspaces/application')
      .then((res) => res.data);
  };

  fetchWorkspaceFiles = (data: Workspaces.FetchWorkspaceFiles.Request) => {
    return this.authenticatedClient
      .get<Workspaces.FetchWorkspaceFiles.Response>(
        `/workspaces/${data.workspaceId}/objects/${data.objectId}`,
        { transformResponse: transformDates }
      )
      .then((res) => res.data);
  };

  fetchWorkspaceFile = (data: { fileId: number }) => {
    return this.authenticatedClient
      .get<Workspaces.FetchWorkspaceFile.Response<string>>(`/workspaces/files/${data.fileId}`)
      .then((res) => ({
        breadcrumbs: res.data.breadcrumbs.map((x) => ({
          ...x,
          createdOn: safeJsonDate(x.createdOn),
          expiresOn: safeIsoDate(x.expiresOn),
        })),
        capabilities: res.data.capabilities,
        meta: res.data.meta,
        object: {
          ...res.data.object,
          createdOn: safeJsonDate(res.data.object.createdOn),
          expiresOn: safeIsoDate(res.data.object.expiresOn),
        },
        file: {
          ...res.data.file,
          createdOn: safeJsonDate(res.data.file.createdOn),
          lastDownloadedOn: safeJsonDate(res.data.file.lastDownloadedOn),
          lastModifiedOn: safeJsonDate(res.data.file.lastModifiedOn),
          publishedOn: safeIsoDate(res.data.file.publishedOn),
        },
        fileVersion: res.data.fileVersion,
        tags: res.data.tags,
        preview: res.data.preview,
        transcript: res.data.transcript,
      }));
  };

  fetchWorkspaceFolder = (data: { folderId: number }) => {
    return this.authenticatedClient
      .get<Workspaces.FetchWorkspaceFolder.Response<string>>(`/workspaces/folders/${data.folderId}`)
      .then((res) => ({
        breadcrumbs: res.data.breadcrumbs.map((x) => ({
          ...x,
          createdOn: safeJsonDate(x.createdOn),
          expiresOn: safeIsoDate(x.expiresOn),
        })),
        object: {
          ...res.data.object,
          createdOn: safeJsonDate(res.data.object.createdOn),
          expiresOn: safeIsoDate(res.data.object.expiresOn),
        },
        folder: {
          ...res.data.folder,
          createdOn: safeJsonDate(res.data.folder.createdOn),
        },
      }));
  };

  getFileSummary = (data: Workspaces.GetFileSummary.Request): Promise<Workspaces.GetFileSummary.Response> => {
    return this.authenticatedClient
      .get<Workspaces.GetFileSummary.Response>(`/workspaces/files/${data.fileId}/summary`)
      .then((res) => res.data);
  };

  getFileParseDebug = (data: { fileId: number }): Promise<WorkspacesAPI.FileParseDebugResponse> => {
    return this.authenticatedClient
      .get(`/workspaces/files/${data.fileId}/parse-debug`)
      .then((res) => WorkspacesAPI.fileParseDebugResponseBodySchema.parse(res.data));
  };

  regenerateFileSummary = (
    data: Workspaces.RegenerateFileSummary.Request
  ): Promise<Workspaces.RegenerateFileSummary.Response> => {
    return this.authenticatedClient
      .post(`/workspaces/files/${data.fileId}/regenerate-summary`, {})
      .then((res) => res.data);
  };

  generateTags = (data: Workspaces.GenerateTags.Request): Promise<Workspaces.GenerateTags.Response> => {
    return this.authenticatedClient
      .post<Workspaces.GenerateTags.Response>(`/workspaces/files/generate-tags`, { fileIds: data.fileIds })
      .then((res) => res.data);
  };

  downloadFileSummary = (data: Workspaces.RegenerateFileSummary.Request) => {
    return download(this.authenticatedClient, `/workspaces/files/${data.fileId}/summary/download`);
  };

  downloadFile = async (data: Workspaces.DownloadFile.Request) => {
    const res = await this.authenticatedClient.get<Workspaces.DownloadFile.Response>(
      `/workspaces/${data.workspaceId}/files/${data.fileId}/download`
    );
    return res.data;
  };

  deleteFile = async (data: Workspaces.DeleteFile.Request) => {
    await this.authenticatedClient.delete(`/workspaces/${data.workspaceId}/files/${data.fileId}`);
  };

  publishFile = async (data: Workspaces.PublishFile.Request) => {
    await this.authenticatedClient.patch(`/workspaces/${data.workspaceId}/files/${data.fileId}/publish`);
  };

  unpublishFile = async (data: Workspaces.UnpublishFile.Request) => {
    await this.authenticatedClient.patch(`/workspaces/${data.workspaceId}/files/${data.fileId}/unpublish`);
  };

  renameFile = async ({
    fileId,
    workspaceId,
    ...data
  }: Workspaces.RenameFile.Body & Workspaces.RenameFile.Params) => {
    await this.authenticatedClient.patch(`/workspaces/${workspaceId}/files/${fileId}/name`, data);
  };

  renameFolder = async ({
    folderId,
    workspaceId,
    ...data
  }: Workspaces.RenameFolder.Body & Workspaces.RenameFolder.Params) => {
    await this.authenticatedClient.patch(`/workspaces/${workspaceId}/folders/${folderId}/name`, data);
  };

  createFolder = async (data: Workspaces.CreateFolder.Body & Workspaces.CreateFolder.Params) => {
    await this.authenticatedClient.put(`/workspaces/${data.workspaceId}/folders`, data);
  };

  deleteFolder = async (data: Workspaces.DeleteFolder.Params) => {
    await this.authenticatedClient.delete(`/workspaces/${data.workspaceId}/folders/${data.folderId}`);
  };
}

export default WorkspacesClient;
